import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/6d089f85-473b-4449-a6e3-5eb88daae93b/images/f216edd259c11b8b8681ac867e3215e8a7e5ab45f0cc3b5cd0736e2f35a844b0.jpeg";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getRoleDest = (role) => {
    if (role === "super_admin") return "/system-admin";
    if (role === "admin") return "/admin";
    if (role === "finance") return "/finance";
    if (role === "operations") return "/property";
    return "/tenant";
  };

  useEffect(() => {
    if (user) {
      navigate(getRoleDest(user.role), { replace: true });
    }
  }, [user, navigate]);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      navigate(getRoleDest(data.role));
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-sky-500" />
        <img src={HERO_IMG} alt="Students" className="w-full h-full object-cover mix-blend-multiply opacity-50" />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <h2 className="font-heading text-4xl font-black text-white tracking-tighter mb-2">Welcome back</h2>
            <p className="text-white/70 text-lg">Sign in to manage your accommodation.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2.5 mb-8">
              <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="font-heading text-lg font-extrabold tracking-tight leading-none text-slate-900">LEGACY</span>
                <span className="font-heading text-[10px] font-bold tracking-[0.3em] block leading-none text-sky-600">HEIGHTS</span>
              </div>
            </Link>
            <h1 className="font-heading text-3xl font-black text-slate-900 tracking-tighter">Sign In</h1>
            <p className="text-slate-500 mt-1">Enter your credentials to access your account.</p>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 mt-1.5 border-slate-200 focus:ring-sky-500 focus:border-sky-500" required data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 mt-1.5 border-slate-200 focus:ring-sky-500 focus:border-sky-500" required data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white rounded-none font-bold text-base" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <Link to="/register" className="text-sky-600 font-semibold hover:underline" data-testid="goto-register">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
